import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Scene from "./Scene";
import { createSpring } from "../spring";
import { useLang } from "../i18n";

function Hero() {
  const { t } = useLang();
  const tiltRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const scene = tiltRef.current;
    const frame = frameRef.current;
    if (!scene || !frame) return undefined;

    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let target = { x: 0, y: 0 };
    const spring = createSpring(
      () => target,
      (x, y) => {
        frame.style.transform = `rotateX(${x}deg) rotateY(${y}deg)`;
      },
    );

    const onMove = (event) => {
      if (!fine.matches || reduce.matches) return;
      const rect = scene.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      target = { x: py * -8, y: px * 8 };
      spring.kick();
    };

    const onLeave = () => {
      target = { x: 0, y: 0 };
      spring.kick();
    };

    scene.addEventListener("pointermove", onMove);
    scene.addEventListener("pointerleave", onLeave);
    return () => {
      spring.stop();
      scene.removeEventListener("pointermove", onMove);
      scene.removeEventListener("pointerleave", onLeave);
      frame.style.transform = "";
    };
  }, []);

  return (
    <section id="about">
      <div className="flex items-center justify-between">
        <p className="text-faint font-mono text-[12px]">src/about.tsx</p>
        <Scene />
      </div>
      <div className="mt-5 grid items-end gap-8 md:grid-cols-[1fr_200px]">
        <div>
          <h1 className="text-[42px] font-medium leading-[1.05] tracking-[-0.035em] sm:text-[56px]">
            Ali Idrees
            <span className="caret" aria-hidden="true" />
          </h1>
          <p className="text-accent mt-3 font-mono text-[13px]">{t.hero.role}</p>
          <p className="text-muted mt-4 max-w-[46ch] text-[15px] leading-7">
            {t.hero.bio}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/projects" className="btn btn-primary">{t.hero.projects}</Link>
            <a href="mailto:aliidrees8600@gmail.com" className="btn btn-ghost">{t.hero.email}</a>
            <a className="btn btn-ghost gap-1.5" href="/CV_Aliidrees_Deutschland.pdf" download="Ali_Idrees_CV.pdf">
              <DownloadIcon />
              {t.hero.cv}
            </a>
          </div>
        </div>
        <div className="tilt" ref={tiltRef}>
          <figure className="photo-frame" ref={frameRef}>
            <figcaption className="photo-caption">
              <span className="live-dot" aria-hidden="true" />
              portrait.jpeg
            </figcaption>
            <img
              src="/portfolio-portrait-green-bg.jpg"
              alt="Ali Idrees"
              className="aspect-[4/5] w-full object-cover"
            />
          </figure>
        </div>
      </div>

      <div className="code-panel mt-10">
        <div className="photo-caption">ali.config.ts</div>
        <pre>
          <code>
            <span className="tok-comment">{t.hero.comment}</span>
            {"\n"}
            <span className="tok-key">export const</span> profile = {"{"}
            {"\n"}
            {"  "}name: <span className="tok-str">&quot;Ali Idrees&quot;</span>,
            {"\n"}
            {"  "}role: <span className="tok-str">&quot;{t.hero.roleValue}&quot;</span>,
            {"\n"}
            {"  "}now: <span className="tok-str">&quot;Brainchimps&quot;</span>,
            {"\n"}
            {"  "}study: <span className="tok-str">&quot;{t.hero.study}&quot;</span>,
            {"\n"}
            {"  "}focus: [{t.hero.focus.map((item, index) => (
              <React.Fragment key={item}>
                {index > 0 ? ", " : null}
                <span className="tok-str">&quot;{item}&quot;</span>
              </React.Fragment>
            ))}],
            {"\n"}
            {"  "}stack: [<span className="tok-str">&quot;NestJS&quot;</span>, <span className="tok-str">&quot;React&quot;</span>, <span className="tok-str">&quot;TypeScript&quot;</span>, <span className="tok-str">&quot;AWS&quot;</span>],
            {"\n"}
            {"}"}
            <span className="tok-punct">;</span>
          </code>
        </pre>
      </div>
    </section>
  );
}

function DownloadIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M12 4v10m0 0 3.5-3.5M12 14 8.5 10.5" />
      <path stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" d="M5 15.5V19h14v-3.5" />
    </svg>
  );
}

export default Hero;
